import React, { useEffect, useState } from "react";
import { BellRing, CheckCircle2, AlertTriangle, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  { key: "sales", label: "Sales inbox", hint: "Low-stock alerts + Monday weekly digest" },
  { key: "community", label: "Community inbox", hint: "New Pup of the Month entries + 1st-of-month winner reminder" },
];

export default function AlertInboxesCard() {
  const [c, setC] = useState({ sales: "", community: "", verified: {} });
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/admin/alert-inboxes").then(({ data }) => setC(data)).catch(() => {}); }, []);
  const save = async () => {
    try { const { data } = await api.put("/admin/alert-inboxes", { sales: c.sales, community: c.community }); setC(data); toast.success("Alert inboxes saved 🔔"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const test = async (which) => {
    setBusy(which);
    try {
      await api.put("/admin/alert-inboxes", { sales: c.sales, community: c.community });
      const { data } = await api.post("/admin/alert-inboxes/test", { which });
      setC((s) => ({ ...s, verified: { ...(s.verified || {}), [which]: { ok: data.ok, error: data.error, email: data.email } } }));
      if (data.ok) toast.success(`Test sent to ${data.email} — check the inbox`);
      else toast.error(`Couldn't deliver to ${data.email}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const cls = "w-full rounded-xl border-2 border-[#19324A]/10 px-3 py-2 text-sm outline-none focus:border-[#FFD93D]";
  return (
    <div className="bg-white rounded-3xl soft-shadow p-6 mb-4" data-testid="alert-inboxes-card">
      <h3 className="font-display font-bold text-[#19324A] mb-1 inline-flex items-center gap-2"><BellRing className="w-4 h-4" /> Alert inboxes</h3>
      <p className="text-xs text-[#19324A]/50 mb-4">Where your admin emails go. Hit <strong>Send test</strong> after saving — if the email service can't reach an address you'll see why right here.</p>
      {c.email_status && (
        <div className={`rounded-2xl px-4 py-3 mb-4 text-xs ${!c.email_status.configured || c.email_status.dry_run ? "bg-[#FF6B9D]/15 text-[#19324A]" : c.email_status.last_error && (!c.email_status.last_ok_at || c.email_status.last_error_at > c.email_status.last_ok_at) ? "bg-[#FFD93D]/40 text-[#19324A]" : "bg-[#7ED957]/25 text-[#19324A]"}`} data-testid="email-status">
          <p className="font-bold inline-flex items-center gap-1.5">
            {!c.email_status.configured ? <><AlertTriangle className="w-3.5 h-3.5" /> Email service key is missing — no emails can be sent</> :
              c.email_status.dry_run ? <><AlertTriangle className="w-3.5 h-3.5" /> Preview dry-run mode: emails are logged, not sent</> :
              <><CheckCircle2 className="w-3.5 h-3.5" /> Email service connected</>}
          </p>
          <p className="mt-1 text-[#19324A]/70" data-testid="email-status-counts">Since last restart: {c.email_status.sent} sent · {c.email_status.failed} failed{c.email_status.last_ok_at ? ` · last delivered ${new Date(c.email_status.last_ok_at).toLocaleString("en-AU")}` : ""}</p>
          {c.email_status.last_error && <p className="mt-1 text-[#19324A]/80 break-words" data-testid="email-status-error">Last error ({new Date(c.email_status.last_error_at).toLocaleString("en-AU")}): {c.email_status.last_error}</p>}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => {
          const v = c.verified?.[f.key];
          return (
            <div key={f.key} data-testid={`alert-inbox-${f.key}`}>
              <label className="block text-xs font-semibold text-[#19324A]/60 mb-1">{f.label} <span className="font-normal">· {f.hint}</span></label>
              <div className="flex gap-2">
                <input type="email" value={c[f.key] || ""} onChange={(e) => setC((s) => ({ ...s, [f.key]: e.target.value }))} placeholder="you@yourdomain.com.au" className={cls} data-testid={`alert-inbox-${f.key}-email`} />
                <button type="button" onClick={() => test(f.key)} disabled={busy === f.key || !c[f.key]} className="shrink-0 px-3 py-2 rounded-xl bg-[#19324A] text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-40" data-testid={`alert-inbox-${f.key}-test`}><Send className="w-3.5 h-3.5" /> {busy === f.key ? "Sending…" : "Send test"}</button>
              </div>
              {v && v.email === c[f.key] && (
                v.ok
                  ? <p className="mt-1.5 text-xs text-[#2E9E5B] font-semibold inline-flex items-center gap-1" data-testid={`alert-inbox-${f.key}-ok`}><CheckCircle2 className="w-3.5 h-3.5" /> Test email accepted — check the inbox (and spam) to confirm.</p>
                  : <p className="mt-1.5 text-xs text-[#C0392B] font-semibold inline-flex items-start gap-1" data-testid={`alert-inbox-${f.key}-error`}><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>Not deliverable: {v.error}</span></p>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-4">
        <button onClick={save} className="btn-pink !py-2" data-testid="alert-inboxes-save">Save</button>
        <p className="text-[11px] text-[#19324A]/45">Tip: if your domain mailbox is rejected, make sure the mailbox actually exists in your email hosting, or use a Gmail/Outlook address in the meantime.</p>
      </div>
    </div>
  );
}
